import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Minus, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";

/**
 * Hard-hitting logistical FAQ that sits directly above the checkout button.
 * Designed to defuse the 3 questions that cause 80%+ of inbound emails so
 * the founder can run this as a true one-person business.
 *
 * Instrumentation: on the FIRST open of each question within a browser session,
 * fire a thin POST /api/track/faq so we can later rank questions by demand and
 * adapt copy. Deduped per session+question via sessionStorage to keep the
 * dataset clean of toggle spam.
 */
const SESSION_KEY = "mp-faq-opened";

const getOpenedSet = () => {
  try {
    const raw = sessionStorage.getItem(SESSION_KEY);
    return new Set(raw ? JSON.parse(raw) : []);
  } catch {
    return new Set();
  }
};

const markOpened = (key) => {
  try {
    const set = getOpenedSet();
    if (set.has(key)) return false;
    set.add(key);
    sessionStorage.setItem(SESSION_KEY, JSON.stringify(Array.from(set)));
    return true;
  } catch {
    return true; // best effort — fire the event anyway
  }
};

// Stable English key for each question (independent of UI language) so the
// backend can aggregate across all 4 locales.
const QUESTION_KEYS = [
  "is_my_letter_private",
  "what_if_email_changes",
  "can_i_delete_letter",
  "no_confirmation_email",
  "why_do_i_pay",
];

const sessionId = (() => {
  try {
    let s = sessionStorage.getItem("mp-session-id");
    if (!s) {
      s = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("mp-session-id", s);
    }
    return s;
  } catch {
    return "s_anon";
  }
})();

export const LogisticsFaq = () => {
  const { t, i18n } = useTranslation();
  const items = t("logistics_faq.items", { returnObjects: true }) || [];
  const [open, setOpen] = useState(-1);
  const trackedRef = useRef(new Set());

  if (!Array.isArray(items) || items.length === 0) return null;

  const onToggle = (i) => {
    const willOpen = i !== open;
    setOpen(willOpen ? i : -1);
    if (!willOpen) return;
    const qKey = QUESTION_KEYS[i] || `q_${i}`;
    // dedupe: only fire once per (session, question)
    if (trackedRef.current.has(qKey)) return;
    if (!markOpened(qKey)) return;
    trackedRef.current.add(qKey);
    api
      .post("/track/faq", {
        question_index: i,
        question_key: qKey,
        language: i18n.language,
        session_id: sessionId,
      })
      .catch(() => {});
  };

  return (
    <div id="faq" data-testid="logistics-faq" className="border border-[var(--rule)] bg-[var(--bg-paper-deep)]/40 px-6 py-7 md:px-8 scroll-mt-24">
      <div className="flex items-center gap-2.5 mb-5 text-[var(--wax)]">
        <ShieldCheck size={15} />
        <p className="text-[11px] uppercase tracking-[0.32em]">
          {t("logistics_faq.title")}
        </p>
      </div>
      <div className="divide-y divide-[var(--rule)]">
        {items.map((item, i) => {
          const isOpen = i === open;
          return (
            <div key={i} className="py-4">
              <button
                type="button"
                onClick={() => onToggle(i)}
                data-testid={`logistics-faq-q-${i}`}
                className="w-full flex justify-between items-start gap-4 text-left group"
              >
                <span className="text-[14px] md:text-[15px] text-[var(--ink)] leading-[1.5] pr-2">{item.q}</span>
                {isOpen ? (
                  <Minus className="text-[var(--wax)] shrink-0 mt-0.5" size={15} />
                ) : (
                  <Plus className="text-[var(--ink-soft)] group-hover:text-[var(--wax)] transition-colors shrink-0 mt-0.5" size={15} />
                )}
              </button>
              <div
                style={{ maxHeight: isOpen ? 600 : 0 }}
                className="overflow-hidden transition-[max-height] duration-500"
              >
                <p className="pt-3 text-[15px] md:text-[16px] text-[var(--ink)] leading-[1.95] italic">{item.a}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default LogisticsFaq;
